import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/typeof';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { useLayoutEffect } from 'react';
import { formHelperTextClasses, filledInputClasses, iconButtonClasses, TextField as TextField$1, InputAdornment } from '@mui/material';
import '../../utils/themeProvider.js';
import { customArcherColors } from '../../utils/customArcherColors.js';
import '@mui/styles';
import '@mui/material/styles';
import 'uuid/v4';
import { Icon } from '../Icon/Icon.js';
import { Typography } from '../Typography/Typography.js';
import '@babel/runtime/helpers/toConsumableArray';
import { useSafeId } from '../../hooks/useSafeId/useSafeId.hook.js';
var _excluded = ["startAdornment", "endAdornment", "label", "placeholder", "required", "error", "helperText", "id", "variant", "readonly", "InputProps", "disabled", "onClick", "autoComplete", "autoFocus", "className", "dataProp", "ariaLabelledBy", "sx"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var useStyles = function useStyles() {
  var _root;

  return {
    root: (_root = {
      flex: '0 1 auto',
      '& >div': {
        maxHeight: '3rem'
      }
    }, _defineProperty(_root, "& .".concat(formHelperTextClasses['contained']), {
      lineHeight: '.75rem',
      bottom: 'unset',
      top: '3rem'
    }), _defineProperty(_root, "& .".concat(filledInputClasses['adornedEnd']), {
      paddingRight: '9.5px'
    }), _defineProperty(_root, "& .".concat(iconButtonClasses['root']), {
      padding: 0
    }), _root),
    adornmentIconStyle: _defineProperty({}, "& .".concat(iconButtonClasses['root']), {
      margin: 0
    }),
    adornmentIconErrorStyle: {
      '& svg': {
        color: customArcherColors.alertColors.error,
        alignSelf: 'center'
      }
    },
    endAdornmentTypography: {
      alignSelf: 'flex-end',
      position: 'relative'
    }
  };
};

var generateAdornment = function generateAdornment(classNames, startAdornment, endAdornment) {
  var error = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var iconStyle = classNames.iconStyle,
      iconErrorStyle = classNames.iconErrorStyle,
      typographyStyle = classNames.typographyStyle;

  var assembleAdornment = function assembleAdornment(adornment, position) {
    var assembledAdornment;

    if (position === 'start') {
      assembledAdornment = adornment;
    }

    if (position === 'end') {
      assembledAdornment = /*#__PURE__*/React.createElement(React.Fragment, null, adornment && typeof adornment === 'string' ? /*#__PURE__*/React.createElement(Typography, {
        sx: typographyStyle
      }, adornment) : null, adornment && _typeof(adornment) === 'object' && adornment.icon && /*#__PURE__*/React.createElement(Icon, {
        icon: adornment.icon,
        fontSize: "large",
        color: "primary"
      }), error ? /*#__PURE__*/React.createElement(Icon, {
        icon: "Error",
        color: "inherit",
        fontSize: "large"
      }) : null);
    }

    return /*#__PURE__*/React.createElement(InputAdornment, {
      sx: error ? iconErrorStyle : iconStyle,
      position: position
    }, assembledAdornment);
  };

  return {
    startAdornment: startAdornment ? assembleAdornment(startAdornment, 'start') : undefined,
    endAdornment: endAdornment || error ? assembleAdornment(endAdornment, 'end') : undefined
  };
};

var TextField = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var startAdornment = _ref.startAdornment,
      endAdornment = _ref.endAdornment,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? '' : _ref$label,
      placeholder = _ref.placeholder,
      required = _ref.required,
      _ref$error = _ref.error,
      error = _ref$error === void 0 ? false : _ref$error,
      helperText = _ref.helperText,
      id = _ref.id,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'filled' : _ref$variant,
      readonly = _ref.readonly,
      _ref$InputProps = _ref.InputProps,
      InputProps = _ref$InputProps === void 0 ? {} : _ref$InputProps,
      disabled = _ref.disabled,
      onClick = _ref.onClick,
      _ref$autoComplete = _ref.autoComplete,
      autoComplete = _ref$autoComplete === void 0 ? 'off' : _ref$autoComplete,
      autoFocus = _ref.autoFocus,
      className = _ref.className,
      dataProp = _ref.dataProp,
      ariaLabelledBy = _ref.ariaLabelledBy,
      sx = _ref.sx,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useStyles = useStyles(),
      root = _useStyles.root,
      adornmentIconStyle = _useStyles.adornmentIconStyle,
      adornmentIconErrorStyle = _useStyles.adornmentIconErrorStyle,
      endAdornmentTypography = _useStyles.endAdornmentTypography;

  var adornmentStyles = {
    iconStyle: adornmentIconStyle,
    iconErrorStyle: adornmentIconErrorStyle,
    typographyStyle: endAdornmentTypography
  };
  var adornmentProps;

  if (error || startAdornment || endAdornment) {
    adornmentProps = generateAdornment(adornmentStyles, startAdornment, endAdornment, error);
  }

  var safeId = useSafeId(id);
  useLayoutEffect(function () {
    if (helperText) {
      var _document$getElementB;

      (_document$getElementB = document.getElementById("".concat(safeId, "-helper-text"))) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.setAttribute('aria-label', helperText);
    }
  }, [helperText, safeId]);
  return /*#__PURE__*/React.createElement(TextField$1, _extends({
    ref: ref
  }, rest, {
    id: safeId,
    error: error,
    required: required,
    disabled: disabled,
    helperText: helperText,
    sx: [root, sx],
    label: label,
    placeholder: placeholder,
    variant: variant,
    onClick: onClick,
    autoComplete: autoComplete,
    autoFocus: autoFocus,
    className: className,
    InputProps: _objectSpread(_objectSpread({}, adornmentProps), {}, {
      readOnly: readonly,
      inputProps: {
        'aria-label': "".concat(label || placeholder),
        'aria-readonly': readonly,
        'data-selector': dataProp ? dataProp : null,
        'aria-labelledby': ariaLabelledBy ? ariaLabelledBy : undefined
      }
    }, InputProps)
  }));
});
TextField.displayName = 'TextField';
TextField.defaultProps = {
  fullWidth: true,
  readonly: false,
  autoFocus: false
};
export { TextField };